"use client";

import Image from "next/image";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { GithubIcon, GlobeIcon, GraduationCap, Linkedin, TwitterIcon } from "lucide-react";

interface Person {
  name: string;
  image: string;
  link: string;
  title: string;
  designation: string;
  linkedIn?: string;
  twitter?: string;
  github?: string;
  personalWebsite?: string;
  googleScholar?: string;
}

export default function FacultyAdvisorCard({ person }: { person: Person }) {
  return (
    <Card className="relative overflow-hidden max-w-xl mx-auto border-primary/15 shadow-sm py-0">
      {/* soft color glow behind the photo, faded outward via blur instead of a CSS gradient */}
      <div className="absolute left-1/2 top-6 -translate-x-1/2 w-80 h-80 rounded-full bg-primary/20 blur-3xl pointer-events-none" />
      <div className="absolute left-1/2 top-6 -translate-x-1/2 w-48 h-48 rounded-full bg-primary/30 blur-2xl pointer-events-none" />

      <CardContent className="relative flex flex-col items-center pt-10 pb-2 px-8">
        <div className="relative w-48 h-48 sm:w-56 sm:h-56 rounded-full overflow-hidden ring-2 ring-primary/30">
          <Image
            src={person.image}
            alt={person.name}
            fill
            sizes="224px"
            className="object-cover object-top"
            onError={(e) => {
              e.currentTarget.src = "/images/placeholder-avatar.svg";
            }}
          />
        </div>
      </CardContent>

      <CardHeader className="relative text-center px-4 pb-0 pt-4 gap-1">
        <CardTitle className="text-xl sm:text-2xl leading-tight">{person.name}</CardTitle>
      </CardHeader>

      <CardFooter className="relative flex justify-center gap-3 px-4 pt-3 pb-6">
        {person.linkedIn && (
          <a href={person.linkedIn} target="_blank" rel="noopener noreferrer">
            <Linkedin className="text-primary hover:scale-110 transition-transform" size={20} />
          </a>
        )}
        {person.twitter && (
          <a href={person.twitter} target="_blank" rel="noopener noreferrer">
            <TwitterIcon className="text-primary hover:scale-110 transition-transform" size={20} />
          </a>
        )}
        {person.github && (
          <a href={person.github} target="_blank" rel="noopener noreferrer">
            <GithubIcon className="text-primary hover:scale-110 transition-transform" size={20} />
          </a>
        )}
        {person.personalWebsite && (
          <a href={person.personalWebsite} target="_blank" rel="noopener noreferrer">
            <GlobeIcon className="text-primary hover:scale-110 transition-transform" size={20} />
          </a>
        )}
        {person.googleScholar && (
          <a href={person.googleScholar} target="_blank" rel="noopener noreferrer">
            <GraduationCap className="text-primary hover:scale-110 transition-transform" size={20} />
          </a>
        )}
      </CardFooter>
    </Card>
  );
}
